<template>
  <el-drawer
    :visible.sync="drawerVisible"
    @close="handleClose"
    direction="btt"
    :with-header="false"
    size="800"
  >
    <div class="top-box">
      <div class="tab-nav">
        <div
          v-for="(tab, index) in tabs"
          :key="tab.key"
          class="tab-item"
          :class="{ active: activeTab === index }"
          @click="activeTab = index"
        >
          <img
            style="width: 48px; height: 48px; margin-bottom: 10px"
            :src="tabIcon(tab)"
            alt=""
          />
          <div>{{ tab.label }}</div>
        </div>
      </div>
      <div class="search-box">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索功能名称"
          clearable
          class="search-input"
          @clear="searchKeyword = ''"
          @keyup.enter.native="() => {}"
        >
          <template slot="append">
            <div class="search-icon"></div>
          </template>
        </el-input>
      </div>
    </div>
    <div class="app-list" v-loading="loadingConfig">
      <div
        v-for="app in filteredTools"
        :key="app.appCode"
        class="app-card"
        @click="openTool(app)"
      >
        <div class="app-left">
          <div class="app-icon">
            <el-image
              v-if="caseImageMap[app.appCode]"
              :src="caseImageMap[app.appCode]"
              fit="cover"
            />
            <div v-else class="app-icon-empty">
              <i class="el-icon-picture-outline" />
            </div>
          </div>
          <div class="app-info">
            <div class="app-name">{{ app.name }}</div>
            <div class="app-desc">{{ caseDescMap[app.appCode] || app.desc }}</div>
          </div>
        </div>
        <div class="footer">
          <div class="app-tag">立即使用</div>
        </div>
      </div>
      <el-empty
        v-if="!loadingConfig && !filteredTools.length"
        description="没有找到匹配的功能"
        :image-size="64"
      />
    </div>
  </el-drawer>
</template>

<script>
import { TOOL_CATEGORIES, TOOL_CATALOG } from "@/data/toolCatalog";
import { listToolApps, listToolCategories } from "@/services/toolApps";

const TAB_ICONS = {};
try {
  const icons = require.context("@/assets/image/", false, /\.(png|jpg|svg)$/);
  icons.keys().forEach((key) => {
    const name = key.replace(/^\.\//, "").replace(/\.\w+$/, "");
    TAB_ICONS[name] = icons(key);
  });
} catch (e) {
  // ignore
}

export default {
  name: "AppDrawer",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchKeyword: "",
      tabs: TOOL_CATEGORIES.slice(),
      activeTab: 0,
      toolCatalog: TOOL_CATALOG.slice(),
      caseImageMap: {},
      caseDescMap: {},
      loadingConfig: false,
      configLoaded: false,
    };
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    activeCategory() {
      return this.tabs[this.activeTab] ? this.tabs[this.activeTab].key : "";
    },
    filteredTools() {
      const keyword = String(this.searchKeyword || "").trim().toLowerCase();
      let list = this.toolCatalog;
      if (this.activeCategory) {
        list = list.filter((t) => t.category === this.activeCategory);
      }
      if (keyword) {
        list = list.filter(
          (t) =>
            t.name.toLowerCase().includes(keyword) ||
            t.desc.toLowerCase().includes(keyword),
        );
      }
      return list;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadDrawerData();
      }
    },
  },
  methods: {
    isImageURL(value) {
      return /^https?:\/\//i.test(String(value || "").trim());
    },
    tabIcon(tab) {
      const icon = tab && tab.icon ? String(tab.icon).trim() : "";
      if (this.isImageURL(icon)) return icon;
      const key = icon || (tab && tab.key) || "";
      return TAB_ICONS[key] || TAB_ICONS["hot"] || "";
    },
    handleClose() {
      this.drawerVisible = false;
    },
    openTool(app) {
      this.handleClose();
      this.$router.push(app.routePath);
    },
    normalizeToolCategory(item) {
      const key = String(item && (item.code || item.key) || "").trim();
      if (!key) return null;
      return {
        key,
        label: String(item.name || item.label || key).trim() || key,
        icon: String(item.icon || key).trim() || key,
      };
    },
    normalizeToolApp(item) {
      const appCode = String(item && (item.app_code || item.appCode) || "").trim();
      if (!appCode) return null;
      const category = item.category && typeof item.category === "object"
        ? String(item.category.code || item.category.key || "").trim()
        : String(item.category || "").trim();
      return {
        appCode,
        category,
        name: String(item.name || appCode).trim() || appCode,
        desc: String(item.description || item.desc || "").trim(),
        routePath: String(item.route_path || item.routePath || "").trim(),
        raw: item,
      };
    },
    applyToolConfig(categories = [], apps = []) {
      const normalizedCategories = categories
        .map((item) => this.normalizeToolCategory(item))
        .filter(Boolean);
      const normalizedApps = apps
        .map((item) => this.normalizeToolApp(item))
        .filter((item) => item && item.routePath);
      if (normalizedCategories.length) {
        this.tabs = normalizedCategories;
        if (this.activeTab >= this.tabs.length) {
          this.activeTab = 0;
        }
      }
      if (normalizedApps.length) {
        const imageMap = {};
        const descMap = {};
        normalizedApps.forEach((app) => {
          const cover = app.raw && app.raw.cover && typeof app.raw.cover === "object" ? app.raw.cover : {};
          const coverImage = String(cover.thumbnail_url || cover.image_url || app.raw.cover_url || "").trim();
          if (coverImage) {
            imageMap[app.appCode] = coverImage;
          }
          const coverSummary = String(cover.summary || "").trim();
          if (coverSummary) {
            descMap[app.appCode] = coverSummary;
          }
        });
        this.toolCatalog = normalizedApps;
        this.caseImageMap = imageMap;
        this.caseDescMap = descMap;
      }
      this.configLoaded = true;
    },
    async loadToolConfig() {
      if (this.configLoaded || this.loadingConfig) return;
      this.loadingConfig = true;
      try {
        const [categoryPayload, appPayload] = await Promise.all([
          listToolCategories({ enabled: true, show_in_app_drawer: true }),
          listToolApps({ enabled: true, show_in_app_drawer: true, slot: "app_drawer_cover" }),
        ]);
        const categories = Array.isArray(categoryPayload && categoryPayload.items) ? categoryPayload.items : [];
        const apps = Array.isArray(appPayload && appPayload.items) ? appPayload.items : [];
        this.applyToolConfig(categories, apps);
      } catch (e) {
        this.configLoaded = true;
      } finally {
        this.loadingConfig = false;
      }
    },
    async loadDrawerData() {
      await this.loadToolConfig();
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-nav {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  gap: 28px;
  padding: 0 42px;
  overflow-x: auto;
}
.tab-item {
  padding: 20px 0;
  text-align: center;
  font-size: 14px;
  color: #777;
  cursor: pointer;
  width: 56px;
  flex-shrink: 0;
}
.tab-item.active {
  color: #333;
  font-weight: 500;
}
.app-list {
  background: #f3f6f9;
  padding: 20px 40px 90px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-content: start;
  align-items: start;
  gap: 20px;
  height: 534px;
  overflow-y: auto;
  .app-card {
    border: 1px solid #e5e5e5;
    background-color: #ffffff;
    border-radius: 12px;
    cursor: pointer;
    transition: box-shadow 0.2s;
    &:hover {
      box-shadow: 3px 0px 30px 0px rgba(131, 156, 185, 0.5);
    }
    .footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      height: 42px;
      padding: 0 17px;
      border-top: solid 1px #eeeeee;
    }
    .app-tag {
      font-size: 12px;
      color: #ff8432;
    }
    .app-left {
      display: flex;
      gap: 12px;
      padding: 14px;
      .app-icon {
        width: 130px;
        height: 130px;
        border-radius: 8px;
        overflow: hidden;
        flex-shrink: 0;
        background: #f5f7fb;
        .el-image {
          width: 100%;
          height: 100%;
        }
      }
      .app-icon-empty {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #b6bfd0;
        font-size: 28px;
      }
      .app-info {
        padding-top: 15px;
        min-width: 0;
        .app-name {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 6px;
        }
        .app-desc {
          font-size: 12px;
          color: #999;
          margin-bottom: 8px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.6;
        }
      }
    }
  }
}
.top-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.search-box {
  width: 400px;
  height: 44px;
  background-color: #ffffff;
  box-shadow: -7px 4px 30px 0px rgba(131, 156, 185, 0.2);
  border: solid 1px #e5e5e5;
  margin-right: 40px;
  flex-shrink: 0;
  ::v-deep .el-input__inner {
    border-radius: 6px 0 0 6px;
    border: none;
  }
  ::v-deep .el-input-group__append {
    background: #fff;
    border-radius: 0 6px 6px 0;
    border: none;
    cursor: pointer;
    .search-icon {
      width: 20px;
      height: 20px;
      background-image: url("@/assets/image/creat-search.png");
      background-size: cover;
    }
  }
}
.search-box:focus-within {
  border: 1px solid #ff8432;
}
</style>
